/**
 * ╔════════════════════════════════════════════════════════════════════════╗
 * ║ api.ts — o ÚNICO arquivo do projeto que conhece o Supabase              ║
 * ╚════════════════════════════════════════════════════════════════════════╝
 *
 * Toda tela importa função daqui; nenhuma importa `@supabase/supabase-js`.
 * `testes/disciplina.test.ts` quebra o build se um segundo arquivo importar.
 *
 * Não é purismo. O convidado fala com TRÊS funções `security definer` e com
 * mais nada — `SELECT` anônimo é negado em todas as tabelas. Se a chamada ao
 * banco estivesse espalhada pelas telas, o dia em que alguém escrevesse
 * `.from('no').select()` na tela do convite seria o dia em que o roteiro
 * inteiro do organizador (as perguntas seguintes, os cenários) passaria a
 * viajar para o navegador de quem só ia confirmar presença. Concentrar aqui é
 * o que torna esse erro visível em revisão.
 *
 * A outra regra: **a tela não decide e não soma.** Roteamento vem de
 * `avancar()`; KPI vem de função SQL. Não escreva agregação aqui.
 */

import { createClient } from '@supabase/supabase-js';
import type { Session, SupabaseClient, User } from '@supabase/supabase-js';

import { exigirConfiguracao } from '../config';
import type {
  Aresta,
  ColunaDeExportacao,
  DefeitoDoGrafo,
  FiltroDaFila,
  Formulario,
  FormularioVersao,
  Grafo,
  LinhaDaFila,
  LinhaDeExportacao,
  Marca,
  No,
  PainelDoFormulario,
  Perfil,
  ResultadoDaRetomada,
  ResultadoDoConvite,
  ResultadoDoFim,
  Valor,
} from '../tipos';

/* ══ Clientes ═════════════════════════════════════════════════════════════ */

let clientePainelMemo: SupabaseClient | null = null;
let clientePublicoMemo: SupabaseClient | null = null;

/**
 * O cliente da prancheta. Guarda a sessão porque a organizadora volta ao painel
 * várias vezes por dia e não deve refazer login a cada aba.
 *
 * O que fica no armazenamento do navegador é o token de sessão emitido pelo
 * Supabase, gerido e rotacionado pelo SDK — não a chave anônima, não a URL,
 * nada que venha do `.env`. Configuração vem do ambiente, sempre.
 */
function painel(): SupabaseClient {
  if (!clientePainelMemo) {
    const c = exigirConfiguracao();
    clientePainelMemo = createClient(c.supabaseUrl, c.supabaseChaveAnonima, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: 'rsvp.sessao',
      },
    });
  }
  return clientePainelMemo;
}

/**
 * O cliente da prancheta, para os serviços que crescem fora deste arquivo
 * (`servicos/gestao.ts`, `servicos/grafo.ts`).
 *
 * Exporta a FUNÇÃO, não o cliente. Um `export const` aqui rodaria
 * `exigirConfiguracao()` no import — antes de `main.tsx` decidir se monta a
 * aplicação — e derrubaria justamente a tela que explica qual variável falta.
 * A construção precisa continuar preguiçosa.
 *
 * A regra segue de pé: quem importa `@supabase/supabase-js` é este arquivo, e
 * só ele. Quem chama recebe o cliente já construído.
 */
export function clientePainel(): SupabaseClient {
  return painel();
}

/**
 * O cliente do convite. Cliente separado, e sem persistência, de propósito:
 *
 *  - o convidado não tem conta e não deve ganhar uma por passar por aqui;
 *  - se o organizador abrir o próprio link no navegador em que está logado, o
 *    cliente compartilhado mandaria o JWT dele junto — e a resposta de teste
 *    passaria por um caminho de permissão que o convidado de verdade não tem.
 *    O bug apareceria só em produção, para quem não pode reportar.
 */
function publico(): SupabaseClient {
  if (!clientePublicoMemo) {
    const c = exigirConfiguracao();
    clientePublicoMemo = createClient(c.supabaseUrl, c.supabaseChaveAnonima, {
      auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
    });
  }
  return clientePublicoMemo;
}

/* ══ Erro ════════════════════════════════════════════════════════════════ */

/**
 * Erro com mensagem para a tela. "Erro diz o que fazer, não pede desculpa" —
 * então a mensagem do Postgres, que é escrita para humano nas funções do
 * motor (`raise exception 'não dá para publicar: %'`), atravessa inteira em
 * vez de virar "algo deu errado".
 */
export class ErroDaApi extends Error {
  readonly codigo: string | null;
  readonly detalhe: string | null;

  constructor(mensagem: string, codigo: string | null = null, detalhe: string | null = null) {
    super(mensagem);
    this.name = 'ErroDaApi';
    this.codigo = codigo;
    this.detalhe = detalhe;
  }
}

type RespostaDoPostgrest<T> = {
  data: T | null;
  error: { message: string; code?: string; details?: string | null } | null;
};

function ouErro<T>(r: RespostaDoPostgrest<T>, oQueFalhou: string): T {
  if (r.error) {
    throw new ErroDaApi(r.error.message, r.error.code ?? null, r.error.details ?? null);
  }
  if (r.data === null || r.data === undefined) {
    throw new ErroDaApi(`${oQueFalhou}: o banco não devolveu nada`);
  }
  return r.data;
}

/**
 * Um `UPDATE` ou `DELETE` que não alcançou linha nenhuma é RECUSA, não sucesso.
 *
 * A assimetria que torna isto necessário é do Postgres, e é fácil de não ver:
 *
 *  - num `INSERT`, a RLS avalia o `with check` contra a linha nova e **recusa
 *    com erro** (`42501`). Quem chamou fica sabendo.
 *  - num `UPDATE`/`DELETE`, o `using` da política entra como **filtro do
 *    `WHERE`**. A linha proibida simplesmente não é encontrada, o comando
 *    termina bem, e o PostgREST devolve `200` com zero linhas. O SDK entrega
 *    `{ error: null }`, e quem só checa `error` conclui que gravou.
 *
 * O resultado é a pior categoria de defeito: a tela escreve "salvo" sobre um
 * banco que não mudou. Foi assim que um proprietário conseguia se rebaixar sem
 * volta e sem aviso — o `update` que o restauraria não fazia nada, em silêncio.
 *
 * Por isso toda escrita daqui pede a linha de volta (`.select()`) e passa por
 * aqui. As que já usavam `.select().single()` estavam protegidas por acidente:
 * o `single()` reprova zero linhas com `PGRST116`.
 *
 * A mensagem não separa "não existe" de "não pode": o PostgREST não distingue
 * os dois, e inventar a distinção seria pior que admiti-la.
 */
export function exigirEfeito(r: RespostaDoPostgrest<readonly unknown[]>, oQueFalhou: string): void {
  if (r.error) {
    throw new ErroDaApi(r.error.message, r.error.code ?? null, r.error.details ?? null);
  }
  if (!r.data || r.data.length === 0) {
    throw new ErroDaApi(
      `${oQueFalhou}: o banco não alterou nada — ou o registro não existe mais, ` +
        'ou o seu papel nesta organização não permite esta ação.',
      'SEM_EFEITO',
    );
  }
}

/* ══ Sessão ══════════════════════════════════════════════════════════════ */

export async function sessaoAtual(): Promise<Session | null> {
  const { data, error } = await painel().auth.getSession();
  if (error) throw new ErroDaApi(error.message);
  return data.session;
}

export async function usuarioAtual(): Promise<User | null> {
  const { data } = await painel().auth.getUser();
  return data.user ?? null;
}

/** Chama de volta a cada mudança de sessão. Devolve a função que cancela. */
export function aoMudarSessao(quando: (sessao: Session | null) => void): () => void {
  const { data } = painel().auth.onAuthStateChange((_evento, sessao) => quando(sessao));
  return () => data.subscription.unsubscribe();
}

export async function entrar(email: string, senha: string): Promise<Session> {
  const { data, error } = await painel().auth.signInWithPassword({ email, password: senha });
  if (error) throw new ErroDaApi(error.message);
  if (!data.session) throw new ErroDaApi('login sem sessão devolvida');
  return data.session;
}

/** Link mágico: o caminho sem senha, para quem organiza um evento e só isso. */
export async function entrarComLink(email: string, voltarPara: string): Promise<void> {
  const { error } = await painel().auth.signInWithOtp({
    email,
    options: { emailRedirectTo: voltarPara },
  });
  if (error) throw new ErroDaApi(error.message);
}

export async function sair(): Promise<void> {
  const { error } = await painel().auth.signOut();
  if (error) throw new ErroDaApi(error.message);
}

/**
 * O perfil da pessoa logada na organização — de onde vem o papel. O papel
 * informa a tela; quem IMPEDE é a RLS. Esconder botão não é controle de
 * acesso, e a política do banco recusa de novo o que a interface já não
 * ofereceu.
 *
 * O filtro por `usuario_id` é o que faz "da pessoa logada" ser verdade.
 *
 * `perfil_ler` (0003) devolve todos os perfis da organização de propósito: a
 * tela Pessoas precisa deles. Sem o filtro, um `select ... limit(1)` traz uma
 * linha qualquer da organização, e numa organização com mais de uma pessoa
 * quase sempre traz a de OUTRA. Foi o que aconteceu no primeiro teste com dois
 * papéis: o operador entrava e a lateral escrevia "Proprietária", o menu
 * oferecia Pessoas e Auditoria, e o detalhe da resposta mostrava "Corrigir".
 * A RLS recusava tudo depois — o dado nunca vazou — mas o clique já tinha
 * parecido possível, que é exatamente o que `ganchos/useSessao.ts` diz existir
 * para evitar.
 */
export async function perfilAtual(): Promise<Perfil | null> {
  const sessao = await sessaoAtual();
  if (!sessao) return null;
  const r = await painel()
    .from('perfil')
    .select('*')
    .eq('usuario_id', sessao.user.id)
    .eq('ativo', true)
    .limit(1)
    .maybeSingle();
  if (r.error) throw new ErroDaApi(r.error.message, r.error.code);
  return (r.data as Perfil | null) ?? null;
}

/* ══ Formulário ══════════════════════════════════════════════════════════ */

export async function listarFormularios(): Promise<Formulario[]> {
  const r = await painel()
    .from('formulario')
    .select('*')
    .order('atualizado_em', { ascending: false });
  return ouErro<Formulario[]>(r, 'listar formulários');
}

export async function obterFormulario(id: string): Promise<Formulario> {
  const r = await painel().from('formulario').select('*').eq('id', id).single();
  return ouErro<Formulario>(r, 'obter formulário');
}

export type NovoFormulario = {
  readonly organizacao_id: string;
  readonly titulo: string;
  readonly descricao?: string;
  readonly marca?: Partial<Marca>;
};

export async function criarFormulario(novo: NovoFormulario): Promise<Formulario> {
  const r = await painel().from('formulario').insert(novo).select('*').single();
  return ouErro<Formulario>(r, 'criar formulário');
}

/** Campos que a tela pode alterar. `estado` e `versao_publicada_id` não estão
 *  aqui: quem os move é `publicarFormulario`, que valida antes. */
export type EdicaoDeFormulario = Partial<
  Pick<
    Formulario,
    | 'titulo'
    | 'descricao'
    | 'marca'
    | 'abertura_em'
    | 'encerramento_em'
    | 'limite_respostas'
    | 'token_publico'
  >
>;

export async function salvarFormulario(
  id: string,
  edicao: EdicaoDeFormulario,
): Promise<Formulario> {
  const r = await painel().from('formulario').update(edicao).eq('id', id).select('*').single();
  return ouErro<Formulario>(r, 'salvar formulário');
}

/**
 * **Existe uma gêmea em `servicos/gestao.ts`.** Esta é a que `modelos/aplicar.ts`
 * usa para desfazer um modelo que falhou; a de lá é a que a fila usa quando
 * alguém clica em excluir.
 *
 * A duplicação é do CLIENTE, não da regra: esta chama `painel()`, que é privado
 * deste arquivo, e por isso não é interceptável pelo dublê dos testes — que
 * mocka o export `clientePainel`. Chamada interna não atravessa a fronteira do
 * módulo, então delegar não resolveria.
 *
 * Quem mexer aqui tem de olhar a de lá, e vice-versa. O que as duas não podem
 * deixar de fazer é passar por `exigirEfeito`: zero linhas apagadas é a RLS
 * recusando, não sucesso.
 */
export async function apagarFormulario(id: string): Promise<void> {
  const r = await painel().from('formulario').delete().eq('id', id).select('id');
  exigirEfeito(r as RespostaDoPostgrest<readonly unknown[]>, 'apagar o formulário');
}

/* ══ Storage da marca ════════════════════════════════════════════════════
 *
 * O logo e a capa do convite. O bucket é criado pela migração 0009, é público
 * na leitura (o convidado é anônimo e não tem quem assine URL para ele) e a
 * escrita é da política, por papel — `scripts/testes/storage.sql` prova as duas
 * metades.
 *
 * Este bloco é onde o toolkit encosta num serviço do Supabase que PODE NÃO
 * ESTAR LÁ: um projeto com o Storage desligado, ou um banco onde a 0009 caiu no
 * caminho degradado por falta de privilégio. A resposta a isso não é lançar —
 * é `storageDisponivel()`, que o editor de marca consulta para dizer na tela
 * por que o envio de imagem não está disponível, e continuar deixando escolher
 * tema, modo, fonte e cor. Estado degradado é dito, nunca escondido.
 */

export const BUCKET_DA_MARCA = 'marca';

/**
 * O bucket existe e responde?
 *
 * `list` com limite 1 é a sonda mais barata que distingue os três casos que
 * importam: bucket ausente, Storage desligado e rede fora. Nenhum deles é
 * defeito da aplicação, e por isso a função devolve `false` em vez de lançar —
 * quem chama precisa de um booleano para desenhar a tela, não de um `catch`.
 *
 * Note que ela NÃO prova que o envio vai funcionar: listar é leitura, e leitura
 * é pública. Um `operador` recebe `true` aqui e a recusa vem depois, do banco,
 * que é onde ela tem de vir — a tela não é controle de acesso.
 */
export async function storageDisponivel(): Promise<boolean> {
  try {
    const { error } = await painel().storage.from(BUCKET_DA_MARCA).list('', { limit: 1 });
    return !error;
  } catch {
    return false;
  }
}

export type CampoDeImagem = 'logo_url' | 'capa_url';

/**
 * O caminho de um envio: `<organizacao>/<formulario>/<campo>-<aleatorio>.<ext>`.
 *
 * A organização vem primeiro porque é o único segmento que a política consegue
 * verificar sem consultar tabela nenhuma — `storage.objects` não tem coluna de
 * organização. O comentário de cabeçalho da 0009 desenvolve.
 *
 * O sufixo aleatório existe porque trocar o logo não pode reaproveitar o nome:
 * CDN e navegador guardam por URL, e o organizador veria o logo antigo depois
 * de trocar. Nome novo a cada envio invalida o cache sem precisar de cabeçalho.
 */
export function caminhoDaImagem(
  organizacaoId: string,
  formularioId: string,
  campo: CampoDeImagem,
  extensao: string,
): string {
  const nome = campo === 'logo_url' ? 'logo' : 'capa';
  const aleatorio = Math.random().toString(36).slice(2, 10);
  return `${organizacaoId}/${formularioId}/${nome}-${aleatorio}.${extensao}`;
}

/** Envia e devolve a URL pública — a que vai para dentro de `marca`. */
export async function enviarImagemDaMarca(caminho: string, arquivo: File): Promise<string> {
  const { error } = await painel().storage.from(BUCKET_DA_MARCA).upload(caminho, arquivo, {
    // `upsert: false` porque o caminho já é único por sorteio: aceitar
    // sobrescrita só abriria a porta para um envio pisar em outro por
    // colisão, e a colisão é o caso em que queremos o erro.
    upsert: false,
    contentType: arquivo.type,
    // Um ano. A URL muda a cada troca (o sufixo aleatório), então cache longo
    // não segura imagem velha — segura imagem certa.
    cacheControl: '31536000',
  });

  if (error) throw new ErroDaApi(error.message);

  const { data } = painel().storage.from(BUCKET_DA_MARCA).getPublicUrl(caminho);
  if (!data.publicUrl) throw new ErroDaApi('o Storage não devolveu a URL pública da imagem');
  return data.publicUrl;
}

/**
 * Apaga o arquivo de uma URL pública nossa. Silencioso por decisão.
 *
 * Quem chama está removendo o logo do formulário, e a gravação de `marca` já
 * aconteceu. Falhar aqui — arquivo já apagado, permissão, rede — não pode
 * desfazer aquilo nem virar erro na tela: o organizador pediu para tirar o logo
 * e o logo saiu do convite. O que sobra é um arquivo órfão no bucket de quem
 * instalou, e trocar isso por uma mensagem de erro que ele não pode resolver
 * seria o negócio ruim.
 *
 * Devolve se apagou, para quem quiser registrar.
 */
export async function removerImagemDaMarca(url: string): Promise<boolean> {
  const caminho = caminhoDaUrlPublica(url);
  if (caminho === null) return false;
  try {
    const { data, error } = await painel().storage.from(BUCKET_DA_MARCA).remove([caminho]);
    // O Storage tem a MESMA armadilha do PostgREST: `remove` de um arquivo que
    // a política não deixa apagar volta sem erro e com a lista VAZIA. Devolver
    // `!error` dizia "apaguei" para toda remoção recusada. Quem decide é o que
    // voltou na lista.
    return !error && (data?.length ?? 0) > 0;
  } catch {
    return false;
  }
}

/**
 * Da URL pública de volta para o caminho dentro do bucket.
 *
 * Devolve `null` para qualquer URL que não seja deste bucket. É o que impede a
 * remoção de tentar apagar um endereço que o organizador tenha colado à mão
 * apontando para outro lugar — o pedido de remoção viraria uma chamada de
 * exclusão contra um caminho inventado.
 */
export function caminhoDaUrlPublica(url: string): string | null {
  const marcador = `/object/public/${BUCKET_DA_MARCA}/`;
  const posicao = url.indexOf(marcador);
  if (posicao < 0) return null;
  const caminho = url.slice(posicao + marcador.length);
  return caminho === '' ? null : decodeURIComponent(caminho);
}

/* ══ Versão e grafo ══════════════════════════════════════════════════════ */

export async function listarVersoes(formularioId: string): Promise<FormularioVersao[]> {
  const r = await painel()
    .from('formulario_versao')
    .select('*')
    .eq('formulario_id', formularioId)
    .order('numero', { ascending: false });
  return ouErro<FormularioVersao[]>(r, 'listar versões');
}

/**
 * A versão em rascunho: a de maior número ainda sem `publicada_em`. É nela que
 * o construtor escreve — publicado é congelado, e editar versão publicada
 * reescreveria o passado de quem já respondeu.
 */
export async function versaoEmRascunho(formularioId: string): Promise<FormularioVersao | null> {
  const r = await painel()
    .from('formulario_versao')
    .select('*')
    .eq('formulario_id', formularioId)
    .is('publicada_em', null)
    .order('numero', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (r.error) throw new ErroDaApi(r.error.message, r.error.code);
  return (r.data as FormularioVersao | null) ?? null;
}

export async function criarVersao(formularioId: string, numero: number): Promise<FormularioVersao> {
  const r = await painel()
    .from('formulario_versao')
    .insert({ formulario_id: formularioId, numero })
    .select('*')
    .single();
  return ouErro<FormularioVersao>(r, 'criar versão');
}

export async function lerGrafo(versaoId: string): Promise<Grafo> {
  const [versao, nos, arestas] = await Promise.all([
    painel().from('formulario_versao').select('*').eq('id', versaoId).single(),
    painel().from('no').select('*').eq('versao_id', versaoId),
    painel().from('aresta').select('*').eq('versao_id', versaoId).order('ordem'),
  ]);
  return {
    versao: ouErro<FormularioVersao>(versao, 'ler versão'),
    nos: ouErro<No[]>(nos, 'ler nós'),
    arestas: ouErro<Aresta[]>(arestas, 'ler arestas'),
  };
}

export type NoParaSalvar = Partial<No> & Pick<No, 'tipo'> & { readonly id?: string };
export type ArestaParaSalvar = Pick<Aresta, 'origem_id' | 'destino_id'> &
  Partial<Pick<Aresta, 'id' | 'condicao' | 'ordem'>>;

/**
 * Nó novo é `insert`; nó que já existe é `update`.
 *
 * Não é rigor semântico: `upsert` com um punhado de colunas manda o Postgres
 * montar a linha COMPLETA do `insert` antes de descobrir o conflito, e a linha
 * incompleta bate no `subtipo_so_em_pergunta` de `0001` — o check que exige
 * `subtipo` exatamente quando o tipo é `pergunta`. O efeito era que renomear
 * uma pergunta, mudar a ajuda, marcar obrigatória ou acrescentar opção voltava
 * 400 e a tela pintava "Não gravou": nenhuma pergunta do produto podia ser
 * editada. Nada disso aparece com serviço de mentira, porque o check é do
 * banco.
 */
export async function salvarNo(versaoId: string, no: NoParaSalvar): Promise<No> {
  const { id, ...campos } = no;
  const r = id
    ? await painel().from('no').update(campos).eq('id', id).select('*').single()
    : await painel()
        .from('no')
        .insert({ ...campos, versao_id: versaoId })
        .select('*')
        .single();
  return ouErro<No>(r, 'salvar nó');
}

/**
 * Recria um nó APAGADO, com o id que ele tinha.
 *
 * É `insert` com id explícito, e não `upsert`: a linha não existe mais — foi
 * apagada — então não há conflito a resolver. `salvarNo` com `id` faz `update`, e
 * `update` numa linha que não existe casa zero linhas e volta erro; foi o que
 * mostrou que esta função tinha de existir.
 *
 * ── Por que o MESMO id, e por que isso é seguro ──────────────────────────────
 *
 * Um id novo pareceria equivalente e não é. `aresta.condicao` guarda o `no_id`
 * da pergunta que ela testa (`{"campo": "<no_id>", ...}`), e é texto dentro de
 * um jsonb — nenhuma chave estrangeira o protege. Recriar com id novo deixaria
 * toda condição que apontava para o nó apagado apontando para o nada: o motor
 * não acha `resposta_item` para aquele campo, `avaliar_condicao` devolve falso, e
 * o formulário passa a rotear TUDO pelo `senão`. Sem erro nenhum, e visível só
 * quando um convidado cai no cenário errado.
 *
 * Reusar o id é seguro porque isto só acontece em RASCUNHO, e rascunho não tem
 * resposta: `abrir_formulario` cria resposta sempre com `versao_publicada_id`
 * (0010), e versão publicada é congelada pela RLS — não há como apagar nó nela.
 * Então nada em `resposta_item`, `resposta_percurso` ou `envio` aponta para o id
 * que voltou.
 *
 * As arestas do nó apagado NÃO voltam com ele: elas caíram por
 * `on delete cascade` e são recriadas uma a uma, por quem chamou. Ver
 * `historico.ts` — é ele que sabe quais eram.
 */
export async function recriarNo(versaoId: string, no: No): Promise<No> {
  const r = await painel()
    .from('no')
    .insert({ ...no, versao_id: versaoId })
    .select('*')
    .single();
  return ouErro<No>(r, 'recriar o nó');
}

export async function apagarNo(id: string): Promise<void> {
  const r = await painel().from('no').delete().eq('id', id).select('id');
  exigirEfeito(r as RespostaDoPostgrest<readonly unknown[]>, 'apagar o nó');
}

/**
 * Posição do nó no canvas. Separado de `salvarNo` porque arrastar dispara
 * dezenas de escritas e não deve carregar o conteúdo do nó junto — nem gerar
 * uma linha de auditoria por pixel.
 */
export async function moverNo(id: string, x: number, y: number): Promise<void> {
  const r = await painel().from('no').update({ x, y }).eq('id', id).select('id');
  exigirEfeito(r as RespostaDoPostgrest<readonly unknown[]>, 'mover o nó');
}

/** Mesma divisão de `salvarNo`, e pelo mesmo motivo: editar é `update`. */
export async function salvarAresta(versaoId: string, aresta: ArestaParaSalvar): Promise<Aresta> {
  const { id, ...campos } = aresta;
  const r = id
    ? await painel().from('aresta').update(campos).eq('id', id).select('*').single()
    : await painel()
        .from('aresta')
        .insert({ ...campos, versao_id: versaoId })
        .select('*')
        .single();
  return ouErro<Aresta>(r, 'salvar aresta');
}

/**
 * Recria uma aresta apagada, com o id que ela tinha. Mesmo motivo de
 * `recriarNo`, e o id importa pela mesma razão de sempre: desfazer duas vezes
 * seguidas tem de encontrar o mundo como o deixou.
 */
export async function recriarAresta(versaoId: string, aresta: Aresta): Promise<Aresta> {
  const r = await painel()
    .from('aresta')
    .insert({ ...aresta, versao_id: versaoId })
    .select('*')
    .single();
  return ouErro<Aresta>(r, 'recriar a saída');
}

export async function apagarAresta(id: string): Promise<void> {
  const r = await painel().from('aresta').delete().eq('id', id).select('id');
  exigirEfeito(r as RespostaDoPostgrest<readonly unknown[]>, 'remover a saída');
}

/* ══ Publicação ══════════════════════════════════════════════════════════ */

/**
 * Diagnóstico sem efeito: roda `validar_grafo` para a tela mostrar os defeitos
 * enquanto se desenha. Rascunho pode estar quebrado; publicado, não.
 */
export async function validarGrafo(versaoId: string): Promise<DefeitoDoGrafo[]> {
  const r = await painel().rpc('validar_grafo', { p_versao_id: versaoId });
  return ouErro<DefeitoDoGrafo[]>(r as RespostaDoPostgrest<DefeitoDoGrafo[]>, 'validar grafo');
}

/**
 * Publicar valida, congela a versão e aponta o formulário para ela. A validação
 * roda DENTRO da função SQL — a tela chamar `validarGrafo` antes é cortesia com
 * quem desenha, não o controle.
 */
export async function publicarFormulario(formularioId: string, versaoId: string): Promise<string> {
  const r = await painel().rpc('publicar_formulario', {
    p_formulario_id: formularioId,
    p_versao_id: versaoId,
  });
  return ouErro<string>(r as RespostaDoPostgrest<string>, 'publicar formulário');
}

/* ══ Respostas e relatório (0006) ════════════════════════════════════════
 *
 * Nada aqui monta `select` na mão, e nada aqui soma. KPI, taxa, fila e
 * exportação vêm de função SQL, respeitando RLS.
 *
 * A forma do retorno é o oposto da do convite: `painel_formulario` devolve UM
 * objeto; as outras três são `returns table` e chegam como **array**. Um `[0]`
 * no lugar errado é o erro que essa assimetria convida.
 */

/** Os KPIs da tela do formulário. Objeto único. */
export async function painelDoFormulario(formularioId: string): Promise<PainelDoFormulario> {
  const r = await painel().rpc('painel_formulario', { p_formulario_id: formularioId });
  return ouErro<PainelDoFormulario>(
    r as RespostaDoPostgrest<PainelDoFormulario>,
    'painel do formulário',
  );
}

/**
 * A fila de respostas, filtrada e paginada no banco.
 *
 * `estado`/`etiqueta` nulos ou omitidos são "sem filtro" — as duas formas se
 * comportam igual, então não há armadilha de default.
 */
export async function listarRespostas(
  formularioId: string,
  filtro: FiltroDaFila = {},
): Promise<LinhaDaFila[]> {
  const r = await painel().rpc('respostas_do_formulario', {
    p_formulario_id: formularioId,
    p_estado: filtro.estado ?? null,
    p_etiqueta: filtro.etiqueta ?? null,
    p_limite: filtro.limite ?? 100,
    p_deslocamento: filtro.deslocamento ?? 0,
  });
  return ouErro<LinhaDaFila[]>(r as RespostaDoPostgrest<LinhaDaFila[]>, 'listar respostas');
}

/**
 * Os cabeçalhos da exportação, por versão. Vem de função separada de propósito:
 * inferir as colunas dos dados faria uma pergunta que ninguém respondeu sumir
 * da planilha.
 */
export async function colunasDeExportacao(formularioId: string): Promise<ColunaDeExportacao[]> {
  const r = await painel().rpc('colunas_de_exportacao', { p_formulario_id: formularioId });
  return ouErro<ColunaDeExportacao[]>(
    r as RespostaDoPostgrest<ColunaDeExportacao[]>,
    'colunas de exportação',
  );
}

/**
 * As linhas da exportação. Recebe o **mesmo filtro** da fila, e não o aplica de
 * novo aqui: §10 manda a exportação respeitar o filtro visível, e duas
 * implementações do mesmo filtro é onde as duas divergem — a planilha traria
 * linha que a lista não mostrava, e ninguém saberia qual está mentindo.
 *
 * Sem paginação: exportar meia página não é exportar.
 *
 * **Agrupe por `versao_numero` na hora de montar a planilha.** Um `Você vem?`
 * da versão 1 e um `Confirma presença?` da versão 2 são perguntas diferentes, e
 * fundir as colunas é a mentira que o versionamento existe para impedir.
 */
export async function exportarRespostas(
  formularioId: string,
  filtro: Pick<FiltroDaFila, 'estado' | 'etiqueta'> = {},
): Promise<LinhaDeExportacao[]> {
  const r = await painel().rpc('exportar_respostas', {
    p_formulario_id: formularioId,
    p_estado: filtro.estado ?? null,
    p_etiqueta: filtro.etiqueta ?? null,
  });
  return ouErro<LinhaDeExportacao[]>(
    r as RespostaDoPostgrest<LinhaDeExportacao[]>,
    'exportar respostas',
  );
}

/**
 * Edição de resposta pelo painel — poder de gestão que o produto oferece de
 * propósito. O `editada = true` e a linha de auditoria com antes/depois saem do
 * trigger, não daqui: dado corrigido em silêncio é dado que ninguém pode mais
 * auditar, e deixar essa marca a cargo do front é deixá-la opcional.
 */
export async function editarItemDeResposta(
  respostaId: string,
  noId: string,
  valor: Valor,
): Promise<void> {
  const { error } = await painel()
    .from('resposta_item')
    .upsert({ resposta_id: respostaId, no_id: noId, valor }, { onConflict: 'resposta_id,no_id' });
  if (error) throw new ErroDaApi(error.message, error.code);
}

// A contagem da fila aguardando transporte (§9) NÃO tem função própria: ela vem
// em `painelDoFormulario` como `envios_pendentes`, junto de `envios_falhos` e da
// quebra por canal. Contar aqui com um `!inner` seria a mesma pergunta feita
// duas vezes, de dois jeitos, com uma chance a mais de a RLS responder diferente.

/* ══ A superfície pública do convite ═════════════════════════════════════
 *
 * Três funções `security definer`, e mais nada — contrato de
 * `0005_api_publica.sql`. Os nomes dos argumentos são casados por PostgREST
 * **por nome**: divergência aqui não é erro de tipo, é 404 em produção.
 *
 * **Recusa NÃO é exceção.** Regra de negócio volta 200 com `{ok: false}`, para
 * o convidado ler "as respostas encerraram na sexta" em vez de "algo deu
 * errado". Só defeito de verdade — rede, permissão, função ausente — vira
 * `ErroDaApi`. Quem chama trata os dois caminhos separados.
 *
 * Não há quarta chamada: `abrirFormulario` já cria a resposta e devolve o
 * `resposta_token` junto do primeiro nó.
 */

/** Devolve o jsonb cru, lançando só no defeito. A recusa passa e é do chamador. */
function ouDefeito<T>(r: RespostaDoPostgrest<T>, oQueFalhou: string): T {
  if (r.error) {
    throw new ErroDaApi(r.error.message, r.error.code ?? null, r.error.details ?? null);
  }
  if (r.data === null || r.data === undefined) {
    throw new ErroDaApi(`${oQueFalhou}: o banco não devolveu nada`);
  }
  return r.data;
}

/**
 * Abre o convite e inicia a resposta.
 *
 * `p_token` aceita as **duas portas**: `formulario.token_publico` (link aberto)
 * ou `convite.token` (link nominal). Uma rota só no front. Se for nominal, a
 * resposta nasce ligada ao convite; reabrir o mesmo link retoma a resposta em
 * andamento — mesmo `resposta_token` — e recusa com `ja_respondido` se ela já
 * tiver chegado ao fim.
 */
export async function abrirFormulario(token: string): Promise<ResultadoDoConvite> {
  const r = await publico().rpc('abrir_formulario', { p_token: token });
  return ouDefeito<ResultadoDoConvite>(
    r as RespostaDoPostgrest<ResultadoDoConvite>,
    'abrir formulário',
  );
}

/**
 * Grava a resposta e avança. Devolve o envelope INTEIRO, não só o próximo nó.
 *
 * `valor` vai cru: o supabase-js serializa para jsonb. `'sim'`, `2`,
 * `['a','c']` e `null` são os quatro casos. Vazio, para o banco, é `null`,
 * `''`, `'   '` e `[]` — pergunta obrigatória recusa os quatro, com `motivo:
 * 'obrigatorio'` e o `no` junto para re-renderizar.
 *
 * Pular pergunta é negado no banco: `p_no_id` diferente do nó atual volta
 * `fora_de_ordem`, não é só a tela que impede.
 */
export async function responder(
  respostaToken: string,
  noId: string,
  valor: Valor,
): Promise<ResultadoDoConvite> {
  const r = await publico().rpc('responder', {
    p_resposta_token: respostaToken,
    p_no_id: noId,
    p_valor: valor,
  });
  return ouDefeito<ResultadoDoConvite>(r as RespostaDoPostgrest<ResultadoDoConvite>, 'responder');
}

/** Fecha, dispara as ações do caminho e devolve o `fim` alcançado. */
export async function concluir(respostaToken: string): Promise<ResultadoDoFim> {
  const r = await publico().rpc('concluir', { p_resposta_token: respostaToken });
  return ouDefeito<ResultadoDoFim>(r as RespostaDoPostgrest<ResultadoDoFim>, 'concluir');
}

/**
 * Devolve a resposta EM ANDAMENTO de um token, com a trilha do que já foi dado.
 *
 * É a chamada do F5 (0010). Antes dela, recarregar no meio do formulário caía em
 * `concluir`, que recusa com `incompleta` — e o gancho, sem outra saída, abria
 * um formulário novo e o convidado perdia o que tinha respondido.
 *
 * Não avança, não grava e não conclui: é leitura da própria resposta, por quem
 * tem o token dela. Serve também DEPOIS do fim, e é o que a tela final usa para
 * se recompor — nesse caso `concluida` volta `true`.
 */
export async function retomar(respostaToken: string): Promise<ResultadoDaRetomada> {
  const r = await publico().rpc('retomar', { p_resposta_token: respostaToken });
  return ouDefeito<ResultadoDaRetomada>(r as RespostaDoPostgrest<ResultadoDaRetomada>, 'retomar');
}
